/* Tenant Branding — post-login personalization for UniBuy™.

   Each authenticated user belongs to a `tenant` (set on the JWT user
   record by `/app/backend/auth.py`). When the user enters the storefront
   (UniBuy™ / Tail-of-the-Tail / Dashboards) we render a *tasteful*
   tenant brand badge so the demo feels personal to that customer —
   without overwriting Infosys's own chrome (the landing page stays
   universal for every login per product brief).

   Catalog data, seed company, and persona keys do NOT change — only the
   displayed customer-side company label is neutralised to "YourCompany"
   so the same demo dataset feels generic to whichever customer is
   driving the screen.
*/

const STATIC = `${process.env.REACT_APP_BACKEND_URL}/api/static/site`;

// Tenant key -> brand record. Keep keys aligned with backend auth.py
// `tenant` field.
export const TENANT_BRANDS = {
  DANONE: {
    name: "Danone",
    tagline: "One Planet. One Health.",
    logo_url: `${STATIC}/danone-logo.png`,
    logo_kind: "image",          // clean logo asset → render at small height
    accent: "#1B6AC9",           // Danone blue
    accent_soft: "rgba(27,106,201,0.08)",
    accent_border: "rgba(27,106,201,0.22)",
  },
  JDECOFFEE: {
    name: "JDE Peet's",
    tagline: "A coffee or tea for every cup.",
    // User provided a marketing photo (not a vector logo). We use it as
    // a square hero/avatar in the welcome card; the top-bar chip uses
    // the text wordmark instead so small sizes stay legible.
    logo_url: `${STATIC}/jde-peets-cafitesse.jpg`,
    logo_kind: "photo",          // photo → only used in the sidebar welcome card
    accent: "#C8102E",           // JDE coffee red
    accent_soft: "rgba(200,16,46,0.07)",
    accent_border: "rgba(200,16,46,0.22)",
  },
  // Aerchain + Infosys Admin get no special brand chrome — they see the
  // default Infosys-only experience.
};

/** Return the brand record for the signed-in user, or `null` for tenants
 *  without bespoke branding (admin, Aerchain).
 *
 *  Accepts a second optional argument — the storefront `session` object
 *  (built from URL params on PunchOut arrivals). PunchOut buyers bypass
 *  the Infosys BPM login gate, so `auth.user` is null; without this
 *  fallback the Danone-via-Coupa flow rendered zero tenant chrome. */
export function getTenantBrand(user, session) {
  const fromUser = user && (user.tenant || "").toUpperCase();
  if (fromUser && TENANT_BRANDS[fromUser]) return TENANT_BRANDS[fromUser];
  const fromSession = session && (session.company_name || "").toUpperCase().trim();
  if (fromSession && TENANT_BRANDS[fromSession]) return TENANT_BRANDS[fromSession];
  return null;
}

/** Display-only neutraliser. The data layer keeps "JDECOFFEE" as the
 *  composite key (orders/carts/profile/dashboard scoping all rely on it)
 *  but every customer-facing render of the company label collapses to
 *  the generic "YourCompany" so Danone, JDE Peet's, and Aerchain logins
 *  all see the same neutral chrome on a shared demo dataset. */
export function displayCompany(rawCompanyName) {
  if (!rawCompanyName) return rawCompanyName;
  const v = String(rawCompanyName).trim().toUpperCase();
  if (v === "JDECOFFEE" || v === "JDECOFFEE.") return "YourCompany";
  // INFOSYS BPM and ACME PHARMA (admin/cross-tenant rollup demo) keep
  // their real labels — they identify the *Infosys* / *example tenant*
  // surfaces and must remain readable in the admin dashboards.
  return rawCompanyName;
}
